// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../utils/adminApi';


// Privacy mode notes for this page:
//   - The import preview tables contain client names/emails/phones for rows
//     that aren't yet portal clients (so no client_id to derive a tag from).
//     The mask functions fall back to hashing the name itself, so the same
//     incoming "John Smith" still consistently renders as "Client XXXX".
//   - We pass the mask functions down into each sub-component so each step
//     can render its own table while staying in sync with the toggle.

const th = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, background: '#0F1838' };
const td = { padding: '8px 12px', fontSize: 13, fontFamily: 'Arial, sans-serif', borderBottom: '1px solid #eee', verticalAlign: 'top' };

function StepBadge({ num, label, status }) {
  const colors = {
    complete: { bg: '#0F1838', color: '#fff', border: '#0F1838' },
    active: { bg: '#2f5496', color: '#fff', border: '#2f5496' },
    locked: { bg: '#f5f5f5', color: '#aaa', border: '#ddd' }
  };
  const c = colors[status] || colors.locked;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 6, border: `1px solid ${c.border}`, background: c.bg, color: c.color, marginBottom: 8 }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: status === 'locked' ? '#ddd' : 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{status === 'complete' ? '✓' : num}</div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14, fontFamily: 'Arial, sans-serif' }}>{label}</div>
        <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', opacity: 0.8 }}>
          {status === 'complete' ? 'Complete' : status === 'active' ? 'Ready' : 'Complete previous step first'}
        </div>
      </div>
    </div>);

}

function FileUploadButton({ label, onFile, disabled }) {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept=".csv" style={{ display: 'none' }}
      onChange={async (e) => {const file = e.target.files[0];if (file) {const text = await file.text();onFile(text);}e.target.value = '';}} />
      <button className="btn btn-primary" onClick={() => ref.current.click()} disabled={disabled}>{label}</button>
    </>);

}

function MattersStep({ session, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  // Default ON because the first thing the user is going to do is the
  // historical bulk import where every new client should land in Closed
  // Files. Untick for an import of brand-new active matters.
  const [importAsClosed, setImportAsClosed] = useState(true);

  async function handleFile(csvText) {
    setLoading(true);setError('');setPreview(null);setResults(null);
    try {const { rows } = await adminApi.previewMatters(csvText);setPreview(rows);setSelected(new Set(rows.map((_, i) => i)));}
    catch (err) {setError(err.message);} finally {setLoading(false);}
  }

  async function handleImport() {
    setLoading(true);setError('');
    try {const r = await adminApi.executeMatters(session.id, preview.filter((_, i) => selected.has(i)), importAsClosed);setResults(r);onComplete();}
    catch (err) {setError(err.message);} finally {setLoading(false);}
  }

  function toggleAll(v) {setSelected(v ? new Set(preview.map((_, i) => i)) : new Set());}
  function toggle(i) {setSelected((p) => {const n = new Set(p);n.has(i) ? n.delete(i) : n.add(i);return n;});}

  // How many of the currently selected rows are NEW (would actually be
  // affected by the importAsClosed flag). Existing duplicates are linked
  // to the session without status changes.
  const newSelectedCount = preview ?
  preview.filter((row, i) => selected.has(i) && !row.exists).length :
  0;

  if (results) return (
    <div>
      <div className="summary-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[['Created', results.created.length, '#0F1838'], ['Linked', results.skipped.length, '#888'], ['Errors', results.errors.length, results.errors.length ? '#B85042' : '#888']].map(([l, v, c]) =>
        <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 28, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
            <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
          </div>
        )}
      </div>
      {results.created.length > 0 &&
      <div className="alert alert-success">
          {results.created.length} account{results.created.length !== 1 ? 's' : ''} created
          {results.importedAsClosed ?
        <> as <strong>Closed Files</strong>. View them on the Closed Files page.</> :
        <> as <strong>Active</strong> clients.</>}
          {' '}Temp password: <strong>ChangeMe1!</strong>
        </div>
      }
      {results.errors.map((e, i) => <div key={i} style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#B85042' }}>{e.name} — {e.reason}</div>)}
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#1e7e5e', marginTop: 12 }}>✓ Step 1 complete. Proceed to Step 2.</p>
    </div>);


  return (
    <div>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 16 }}>Export from Clio: <strong>Matters → Export as CSV</strong>. Existing clients are linked without creating duplicates.</p>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Processing...</div>}
      {!preview && !loading && <FileUploadButton label="Upload Matters CSV" onFile={handleFile} />}
      {preview && !loading &&
      <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, margin: 0 }}><strong>{preview.length}</strong> clients found — <strong>{selected.size}</strong> selected</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" onClick={() => toggleAll(true)}>Select All</button>
              <button className="btn btn-sm" onClick={() => toggleAll(false)}>Deselect All</button>
              <button className="btn btn-sm" onClick={() => {setPreview(null);setSelected(new Set());}}>Change File</button>
            </div>
          </div>
          <div className="table-wrap" style={{ marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...th, width: 40 }}><input type="checkbox" checked={selected.size === preview.length} onChange={(e) => toggleAll(e.target.checked)} /></th>
                <th style={th}>Client Name</th><th style={th}>Matter No.</th><th style={th}>Description</th><th style={th}>Status</th>
              </tr></thead>
              <tbody>{preview.map((row, i) =>
              <tr key={i} style={{ background: selected.has(i) ? '#fff' : '#F5F1E8', cursor: 'pointer' }} onClick={() => toggle(i)}>
                  <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} onClick={(e) => e.stopPropagation()} /></td>
                  <td style={{ ...td, fontWeight: 500 }}>{row.name}</td>
                  <td style={td}>{row.matterNumber}</td>
                  <td style={{ ...td, maxWidth: 240 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</div></td>
                  <td style={td}>{row.exists ? <span style={{ fontSize: 11, background: '#fff8e1', color: '#b7870a', border: '1px solid #f5d87a', borderRadius: 3, padding: '2px 7px' }}>Already in portal</span> : <span style={{ fontSize: 11, background: '#f0faf5', color: '#1e7e5e', border: '1px solid #b7e3cf', borderRadius: 3, padding: '2px 7px' }}>New</span>}</td>
                </tr>
              )}</tbody>
            </table>
          </div>
          <div className="alert alert-info" style={{ marginBottom: 12 }}>New accounts use temp password <strong>ChangeMe1!</strong></div>

          {/* Closed Files toggle. Only affects rows marked "New" — duplicates
             are linked to the session without status changes regardless. */}
          <div style={{
          border: '1px solid #ddd', borderRadius: 6, padding: 14, marginBottom: 12,
          background: importAsClosed ? '#fdf2e7' : '#fafafa'
        }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
              type="checkbox"
              checked={importAsClosed}
              onChange={(e) => setImportAsClosed(e.target.checked)}
              style={{ marginTop: 3, flexShrink: 0 }} />
            
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                <strong>Mark new clients from this import as Closed Files</strong>
                <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
                  {importAsClosed ?
                <>The <strong>{newSelectedCount}</strong> new client{newSelectedCount === 1 ? '' : 's'} in this batch will be created with <strong>Closed</strong> status and land in the Closed Files page, not the active client list. Their portal access will expire 6 months from today. Already-existing clients in this CSV are linked to the session without any status change.</> :
                <>The <strong>{newSelectedCount}</strong> new client{newSelectedCount === 1 ? '' : 's'} in this batch will be created as <strong>Active</strong> clients and appear in the All Clients list. Uncheck this for ongoing imports of historical matters.</>}
                </div>
              </div>
            </label>
          </div>

          <div className="btn-row">
            <button className="btn" onClick={() => {setPreview(null);setSelected(new Set());}}>Cancel</button>
            <button className="btn btn-primary" onClick={handleImport} disabled={!selected.size || loading}>
              Import {selected.size} Client{selected.size !== 1 ? 's' : ''}{importAsClosed ? ' as Closed Files' : ''}
            </button>
          </div>
        </>
      }
    </div>);

}

function ContactsStep({ session, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  async function handleFile(csvText) {
    setLoading(true);setError('');setPreview(null);setResults(null);
    try {setPreview(await adminApi.previewContacts(session.id, csvText));}
    catch (err) {setError(err.message);} finally {setLoading(false);}
  }

  async function handleImport() {
    setLoading(true);setError('');
    try {setResults(await adminApi.executeContacts(session.id, preview.matches));onComplete();}
    catch (err) {setError(err.message);} finally {setLoading(false);}
  }

  if (results) return (
    <div>
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[['Updated', results.updated, '#0F1838'], ['Skipped', results.skipped, '#888']].map(([l, v, c]) =>
        <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 28, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
            <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
          </div>
        )}
      </div>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#1e7e5e', marginTop: 12 }}>✓ Step 2 complete. Proceed to Step 3.</p>
    </div>);


  return (
    <div>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 16 }}>Export from Clio: <strong>Contacts → Export as CSV</strong>. Only the {session.clients?.length || 0} clients from Step 1 will be updated.</p>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Matching contacts...</div>}
      {!preview && !loading && <FileUploadButton label="Upload Contacts CSV" onFile={handleFile} />}
      {preview && !loading &&
      <>
          <div className="summary-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[['Will Update', preview.matches.filter((m) => m.willUpdate).length, '#0F1838'], ['Skip (has email)', preview.matches.filter((m) => !m.willUpdate).length, '#888'], ['Not in session', preview.unmatched.length, '#888']].map(([l, v, c]) =>
          <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 12 }}>
                <div style={{ fontSize: 24, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
              </div>
          )}
          </div>
          {preview.matches.length > 0 &&
        <div className="table-wrap" style={{ marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Client</th><th style={th}>New Email</th><th style={th}>New Phone</th><th style={th}>Action</th></tr></thead>
                <tbody>{preview.matches.map((m, i) =>
              <tr key={i} style={{ background: m.willUpdate ? '#fff' : '#fafafa' }}>
                    <td style={{ ...td, fontWeight: 500 }}>{m.clientName}</td>
                    <td style={td}>{m.newEmail}</td>
                    <td style={td}>{m.newPhone ? m.newPhone : '—'}</td>
                    <td style={td}>{m.willUpdate ? <span style={{ fontSize: 11, background: '#f0faf5', color: '#1e7e5e', border: '1px solid #b7e3cf', borderRadius: 3, padding: '2px 7px' }}>Update</span> : <span style={{ fontSize: 11, background: '#f5f5f5', color: '#888', borderRadius: 3, padding: '2px 7px' }}>Skip</span>}</td>
                  </tr>
              )}</tbody>
              </table>
            </div>
        }
          <div className="btn-row">
            <button className="btn" onClick={() => setPreview(null)}>Change File</button>
            <button className="btn btn-primary" onClick={handleImport} disabled={loading || !preview.matches.filter((m) => m.willUpdate).length}>Update {preview.matches.filter((m) => m.willUpdate).length} Clients</button>
          </div>
        </>
      }
    </div>);

}

function ActivitiesStep({ session, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  async function handleFile(csvText) {
    setLoading(true);setError('');setPreview(null);setResults(null);
    try {setPreview(await adminApi.previewActivities(session.id, csvText));}
    catch (err) {setError(err.message);} finally {setLoading(false);}
  }

  async function handleImport() {
    setLoading(true);setError('');
    try {setResults(await adminApi.executeActivities(session.id, preview.matched));onComplete();}
    catch (err) {setError(err.message);} finally {setLoading(false);}
  }

  if (results) return (
    <div>
      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[['Imported', results.imported, '#0F1838'], ['Errors', results.errors, results.errors ? '#B85042' : '#888']].map(([l, v, c]) =>
        <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 16 }}>
            <div style={{ fontSize: 28, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
            <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
          </div>
        )}
      </div>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#1e7e5e', marginTop: 12 }}>✓ Import complete! Billing entries are now visible in each client's profile.</p>
    </div>);


  return (
    <div>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 16 }}>Export from Clio: <strong>Reports → Activities → Export as CSV</strong>. Only entries for the {session.clients?.length || 0} matters from Step 1 are imported. Duplicates are skipped automatically.</p>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Analyzing activities...</div>}
      {!preview && !loading && <FileUploadButton label="Upload Activities CSV" onFile={handleFile} />}
      {preview && !loading &&
      <>
          <div className="summary-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[['Will Import', preview.matched.length, '#0F1838'], ['Duplicates Skipped', preview.duplicates, '#888'], ['No Match', preview.unmatched, '#888']].map(([l, v, c]) =>
          <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 12 }}>
                <div style={{ fontSize: 24, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
              </div>
          )}
          </div>
          {preview.matched.length > 0 &&
        <div className="table-wrap" style={{ marginBottom: 16, maxHeight: 400, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Client</th><th style={th}>Date</th><th style={th}>Hours</th><th style={th}>Description</th><th style={th}>Billable</th></tr></thead>
                <tbody>{preview.matched.map((a, i) =>
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ ...td, fontWeight: 500 }}>{a.clientName}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{a.date}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{a.hours}</td>
                    <td style={{ ...td, maxWidth: 260 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.description}</div></td>
                    <td style={td}><span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 3, background: a.billable ? '#f0faf5' : '#F5F1E8', color: a.billable ? '#1e7e5e' : '#888', border: `1px solid ${a.billable ? '#b7e3cf' : '#ddd'}` }}>{a.billable ? 'Billable' : 'Non-bill'}</span></td>
                  </tr>
              )}</tbody>
              </table>
            </div>
        }
          <div className="btn-row">
            <button className="btn" onClick={() => setPreview(null)}>Change File</button>
            <button className="btn btn-primary" onClick={handleImport} disabled={loading || !preview.matched.length}>Import {preview.matched.length} Entries</button>
          </div>
        </>
      }
    </div>);

}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 4 — BILLS (Phase 4 Clio import)
// ─────────────────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  const num = parseFloat(n) || 0;
  return '$' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const BILL_STATUS_STYLES = {
  issued: { bg: '#e8f0fb', fg: '#2f5496', border: '#bcd4ee', label: 'Issued' },
  partial: { bg: '#fdf2e7', fg: '#d68910', border: '#f5c98e', label: 'Partial' },
  paid: { bg: '#e8f7ef', fg: '#1a7a5e', border: '#b8e3cf', label: 'Paid' },
  voided: { bg: '#f0f0f0', fg: '#888', border: '#dcdcdc', label: 'Voided' }
};
function StatusBadge({ status }) {
  const s = BILL_STATUS_STYLES[status] || BILL_STATUS_STYLES.issued;
  return (
    <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 3, background: s.bg, color: s.fg, border: `1px solid ${s.border}`, fontFamily: 'Arial, sans-serif' }}>
      {s.label}
    </span>);

}

function BillsStep({ session, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  async function handleFile(csvText) {
    setLoading(true);setError('');setPreview(null);setResults(null);setSelected(new Set());
    try {
      const r = await adminApi.previewBills(session.id, csvText);
      setPreview(r);
      setSelected(new Set(r.matched.map((_, i) => i)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    setLoading(true);setError('');
    try {
      const bills = preview.matched.filter((_, i) => selected.has(i));
      const r = await adminApi.executeBills(session.id, bills);
      setResults(r);
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleAll(v) {
    setSelected(v ? new Set(preview.matched.map((_, i) => i)) : new Set());
  }
  function toggle(i) {
    setSelected((p) => {const n = new Set(p);n.has(i) ? n.delete(i) : n.add(i);return n;});
  }

  if (results) return (
    <div>
      <div className="summary-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[
        ['Imported', results.imported, '#0F1838'],
        ['Already Imported', results.skipped, '#888'],
        ['Errors', results.errors?.length || 0, results.errors?.length || 0 ? '#B85042' : '#888']].
        map(([l, v, c]) =>
        <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 12 }}>
            <div style={{ fontSize: 24, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
            <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
          </div>
        )}
      </div>
      {results.errors?.length > 0 &&
      <div className="alert alert-error" style={{ marginBottom: 12 }}>
          {results.errors.map((e, i) =>
        <div key={i} style={{ fontFamily: 'Arial, sans-serif', fontSize: 13 }}>Clio bill #{e.clioBillId}: {e.reason}</div>
        )}
        </div>
      }
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#1e7e5e' }}>
        ✓ Bills imported. They appear under each client's Billing tab as portal invoices, with their final state baked in.
      </p>
    </div>);


  return (
    <div>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 16 }}>
        Export from Clio: <strong>Bills → Export as CSV</strong>. Bills are matched to portal clients firm-wide
        by Clio matter number. Bills you've imported before are detected automatically and won't duplicate.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Analyzing bills...</div>}
      {!preview && !loading && <FileUploadButton label="Upload Bills CSV" onFile={handleFile} />}
      {preview && !loading &&
      <>
          <div className="summary-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[
          ['Will Import', preview.matched.length, '#0F1838'],
          ['Selected', selected.size, '#2f5496'],
          ['Already Imported', preview.alreadyImported.length, '#888'],
          ['Orphans', preview.orphans.length, preview.orphans.length ? '#b7870a' : '#888']].
          map(([l, v, c]) =>
          <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 12 }}>
                <div style={{ fontSize: 24, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
              </div>
          )}
          </div>

          {preview.orphans.length > 0 &&
        <div className="alert" style={{ background: '#fff8e1', border: '1px solid #f5d87a', color: '#7a5800', marginBottom: 16 }}>
              <strong>{preview.orphans.length} bill{preview.orphans.length === 1 ? '' : 's'} could not be matched to a portal client.</strong>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                These bills reference matter numbers that aren't on file. Import the matter via Step 1 in any session, then re-run this step.
              </div>
              <ul style={{ marginTop: 8, marginBottom: 0, fontSize: 12, fontFamily: 'Arial, sans-serif' }}>
                {preview.orphans.slice(0, 8).map((o, i) =>
            <li key={i}>
                    Clio #{o.clioBillId} — matter <strong>{o.matterNumber || '(missing)'}</strong>
                    {o.clientName ? ` — "${o.clientName}"` : ''} — {fmtMoney(o.total)} ({o.state || 'unknown state'})
                  </li>
            )}
                {preview.orphans.length > 8 && <li>…and {preview.orphans.length - 8} more</li>}
              </ul>
            </div>
        }

          {preview.alreadyImported.length > 0 &&
        <div className="alert" style={{ background: '#f0f0f0', border: '1px solid #dcdcdc', color: '#555', marginBottom: 16, fontSize: 12 }}>
              {preview.alreadyImported.length} bill{preview.alreadyImported.length === 1 ? '' : 's'} already in the portal. Re-importing is safe — they'll be skipped.
            </div>
        }

          {preview.matched.length > 0 &&
        <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, margin: 0 }}>
                  <strong>{preview.matched.length}</strong> bill{preview.matched.length === 1 ? '' : 's'} ready — <strong>{selected.size}</strong> selected
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm" onClick={() => toggleAll(true)}>Select All</button>
                  <button className="btn btn-sm" onClick={() => toggleAll(false)}>Deselect All</button>
                  <button className="btn btn-sm" onClick={() => {setPreview(null);setSelected(new Set());}}>Change File</button>
                </div>
              </div>
              <div className="table-wrap" style={{ marginBottom: 16, maxHeight: 420, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...th, width: 36 }}>
                      <input type="checkbox" checked={selected.size === preview.matched.length} onChange={(e) => toggleAll(e.target.checked)} />
                    </th>
                    <th style={th}>Clio #</th>
                    <th style={th}>Client</th>
                    <th style={th}>Matter</th>
                    <th style={th}>Issue</th>
                    <th style={th}>Status</th>
                    <th style={{ ...th, textAlign: 'right' }}>Total</th>
                    <th style={{ ...th, textAlign: 'right' }}>Paid</th>
                    <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                  </tr></thead>
                  <tbody>{preview.matched.map((b, i) =>
                <tr key={i} onClick={() => toggle(i)}
                style={{ background: selected.has(i) ? '#fff' : '#f6f6f6', cursor: 'pointer' }}>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} onClick={(e) => e.stopPropagation()} />
                      </td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{b.clioBillId}</td>
                      <td style={{ ...td, fontWeight: 500 }}>{b.clientName}</td>
                      <td style={{ ...td, color: '#555' }}>{b.matterNumber}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: '#555' }}>{b.issueDate || '—'}</td>
                      <td style={td}><StatusBadge status={b.portalStatus} /></td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(b.total)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#555' }}>{fmtMoney(b.balancePaid)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtMoney(b.total - b.balancePaid)}</td>
                    </tr>
                )}</tbody>
                </table>
              </div>
              <div className="alert alert-info" style={{ marginBottom: 12, fontSize: 12 }}>
                Each imported bill gets a new portal invoice number (1001+) plus a "Clio #X" reference. The portal renders one synthesized line item ("Legal services through [Issue Date]") so PDFs and emails look normal.
              </div>
              <div className="btn-row">
                <button className="btn" onClick={() => {setPreview(null);setSelected(new Set());}}>Cancel</button>
                <button className="btn btn-primary" onClick={handleImport} disabled={!selected.size || loading}>
                  Import {selected.size} Bill{selected.size === 1 ? '' : 's'}
                </button>
              </div>
            </>
        }

          {preview.matched.length === 0 && preview.orphans.length === 0 && preview.alreadyImported.length === 0 &&
        <div className="card" style={{ textAlign: 'center', color: '#888', padding: 24, fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
              No bills found in this CSV.
            </div>
        }
        </>
      }
    </div>);

}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 5 — TRUST TRANSACTIONS (Phase 4 Clio import)
// ─────────────────────────────────────────────────────────────────────────────

const TRUST_TYPE_LABELS = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  transfer_to_operating: 'Transfer to Operating',
  adjustment: 'Adjustment'
};
function TypeBadge({ type }) {
  const colors = {
    deposit: { bg: '#e8f7ef', fg: '#1a7a5e', border: '#b8e3cf' },
    withdrawal: { bg: '#fdf0ec', fg: '#B85042', border: '#f5c6c3' },
    transfer_to_operating: { bg: '#fdf2e7', fg: '#d68910', border: '#f5c98e' },
    adjustment: { bg: '#f0f0f0', fg: '#555', border: '#dcdcdc' }
  };
  const c = colors[type] || colors.adjustment;
  return (
    <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 3, background: c.bg, color: c.fg, border: `1px solid ${c.border}`, fontFamily: 'Arial, sans-serif' }}>
      {TRUST_TYPE_LABELS[type] || type}
    </span>);

}

function TrustStep({ session, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  async function handleFile(csvText) {
    setLoading(true);setError('');setPreview(null);setResults(null);
    try {
      const r = await adminApi.previewTrust(session.id, csvText);
      setPreview(r);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    setLoading(true);setError('');
    try {
      const r = await adminApi.executeTrust(session.id, preview.matched);
      setResults(r);
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (results) return (
    <div>
      <div className="summary-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[
        ['Imported', results.imported, '#0F1838'],
        ['Already Imported', results.skipped, '#888'],
        ['Clients Touched', results.clientsTouched, '#2f5496'],
        ['Errors', results.errors?.length || 0, results.errors?.length || 0 ? '#B85042' : '#888']].
        map(([l, v, c]) =>
        <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 12 }}>
            <div style={{ fontSize: 24, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
            <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
          </div>
        )}
      </div>
      {results.errors?.length > 0 &&
      <div className="alert alert-error" style={{ marginBottom: 12 }}>
          {results.errors.map((e, i) =>
        <div key={i} style={{ fontFamily: 'Arial, sans-serif', fontSize: 13 }}>{e.clientName} ({e.txnDate}): {e.reason}</div>
        )}
        </div>
      }
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#1e7e5e' }}>
        ✓ Trust ledger updated. View the firm-wide balance under Trust Accounting in the sidebar, or each client's register on their Billing tab.
      </p>
    </div>);


  return (
    <div>
      <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555', marginBottom: 16 }}>
        Export from Clio: <strong>Reports → Trust Transactions → Export as CSV</strong>. Each row in the CSV becomes one ledger entry. Re-running the same CSV is safe — duplicates are detected and skipped.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Analyzing transactions...</div>}
      {!preview && !loading && <FileUploadButton label="Upload Trust Transaction CSV" onFile={handleFile} />}
      {preview && !loading &&
      <>
          <div className="summary-grid-4" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[
          ['Will Import', preview.matched.length, '#0F1838'],
          ['Already Imported', preview.alreadyImported.length, '#888'],
          ['Orphans', preview.orphans.length, preview.orphans.length ? '#b7870a' : '#888']].
          map(([l, v, c]) =>
          <div key={l} className="card" style={{ margin: 0, textAlign: 'center', padding: 12 }}>
                <div style={{ fontSize: 24, fontFamily: 'Arial, sans-serif', color: c }}>{v}</div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase' }}>{l}</div>
              </div>
          )}
          </div>

          {preview.projectedDelta?.length > 0 &&
        <div className="card" style={{ padding: 14, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontFamily: 'Arial, sans-serif', fontSize: 13, marginBottom: 8 }}>
                Projected balance changes
              </div>
              <div style={{ maxHeight: 140, overflowY: 'auto', fontSize: 12, fontFamily: 'Arial, sans-serif' }}>
                {preview.projectedDelta.slice(0, 30).map((p, i) =>
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span>{p.clientName}</span>
                    <span style={{ fontFamily: 'monospace', color: p.delta >= 0 ? '#1a7a5e' : '#B85042' }}>
                      {p.delta >= 0 ? '+' : ''}{fmtMoney(p.delta)}
                    </span>
                  </div>
            )}
                {preview.projectedDelta.length > 30 &&
            <div style={{ color: '#888', marginTop: 4 }}>…and {preview.projectedDelta.length - 30} more</div>
            }
              </div>
            </div>
        }

          {preview.orphans.length > 0 &&
        <div className="alert" style={{ background: '#fff8e1', border: '1px solid #f5d87a', color: '#7a5800', marginBottom: 16 }}>
              <strong>{preview.orphans.length} transaction{preview.orphans.length === 1 ? '' : 's'} could not be matched.</strong>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                The client name or matter number in these rows isn't on file. Add the matter (Step 1) and re-run.
              </div>
              <ul style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                {preview.orphans.slice(0, 6).map((o, i) =>
            <li key={i}>
                    {o.clientName ? o.clientName : '(no name)'} {o.matterNumber ? `(${o.matterNumber})` : ''} — {o.txnDate} — in {fmtMoney(o.fundsIn || 0)}, out {fmtMoney(o.fundsOut || 0)} — {o.reason}
                  </li>
            )}
                {preview.orphans.length > 6 && <li>…and {preview.orphans.length - 6} more</li>}
              </ul>
            </div>
        }

          {preview.matched.length > 0 &&
        <div className="table-wrap" style={{ marginBottom: 16, maxHeight: 420, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Date</th>
                  <th style={th}>Client</th>
                  <th style={th}>Type</th>
                  <th style={th}>Description</th>
                  <th style={th}>Reference</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                </tr></thead>
                <tbody>{preview.matched.map((t, i) =>
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{t.txnDate}</td>
                    <td style={{ ...td, fontWeight: 500 }}>{t.clientName}</td>
                    <td style={td}><TypeBadge type={t.type} /></td>
                    <td style={{ ...td, maxWidth: 280 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.description ? t.description : '—'}
                        {t.payerPayee && <span style={{ color: '#888', marginLeft: 6 }}>({t.payerPayee})</span>}
                      </div>
                    </td>
                    <td style={{ ...td, color: '#555' }}>{t.reference || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: t.amount >= 0 ? '#1a7a5e' : '#B85042' }}>
                      {t.amount >= 0 ? '+' : ''}{fmtMoney(t.amount)}
                    </td>
                  </tr>
              )}</tbody>
              </table>
            </div>
        }

          <div className="btn-row">
            <button className="btn" onClick={() => setPreview(null)}>Change File</button>
            <button className="btn btn-primary" onClick={handleImport} disabled={loading || !preview.matched.length}>
              Import {preview.matched.length} Transaction{preview.matched.length === 1 ? '' : 's'}
            </button>
          </div>
        </>
      }
    </div>);

}

export default function AdminImportPage() {
  // pass into sub-components as a bag
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {loadSessions();}, []);

  async function loadSessions() {
    try {setSessions(await adminApi.getSessions());setLoading(false);}
    catch (err) {setError(err.message);setLoading(false);}
  }

  async function openSession(id) {
    const data = await adminApi.getSession(id);
    setActive(data);
    if (!data.matters_imported_at) setActiveStep(1);else
    if (!data.contacts_imported_at) setActiveStep(2);else
    if (!data.activities_imported_at) setActiveStep(3);else
    if (!data.bills_imported_at) setActiveStep(4);else
    if (!data.trust_imported_at) setActiveStep(5);else
    setActiveStep(4); // all complete — default to a useful tab
  }

  async function createSession() {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {const s = await adminApi.createSession(newLabel.trim());await loadSessions();openSession(s.id);setNewLabel('');}
    catch (err) {setError(err.message);} finally {setCreating(false);}
  }

  async function refreshSession() {
    if (activeSession) {const data = await adminApi.getSession(activeSession.id);setActive(data);await loadSessions();}
  }

  function stepStatus(step) {
    if (!activeSession) return 'locked';
    if (step === 1) return activeSession.matters_imported_at ? 'complete' : 'active';
    if (step === 2) return !activeSession.matters_imported_at ? 'locked' : activeSession.contacts_imported_at ? 'complete' : 'active';
    if (step === 3) return !activeSession.contacts_imported_at ? 'locked' : activeSession.activities_imported_at ? 'complete' : 'active';
    // Steps 4 and 5 only require Step 1 (matters → clients with clio_matter_number).
    // Bills and trust are matched firm-wide by matter number, not session-scoped.
    if (step === 4) return !activeSession.matters_imported_at ? 'locked' : activeSession.bills_imported_at ? 'complete' : 'active';
    if (step === 5) return !activeSession.matters_imported_at ? 'locked' : activeSession.trust_imported_at ? 'complete' : 'active';
    return 'locked';
  }

  if (activeSession) return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="btn btn-sm" onClick={() => {setActive(null);loadSessions();}}>← All Sessions</button>
        <h1 className="page-title" style={{ margin: 0 }}>{activeSession.label}</h1>
      </div>
      <p className="page-subtitle">Complete all three steps in order to fully import your Clio data.</p>
      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {[
        ['1', 'Matters → Clients', 2],
        ['2', 'Contacts → Emails & Phones', 3],
        ['3', 'Activities → Billing', 4],
        ['4', 'Bills → Portal Invoices', 5],
        ['5', 'Trust Transactions → Ledger', 6]].
        map(([n, label, s]) =>
        <div key={n} onClick={() => stepStatus(parseInt(n)) !== 'locked' && setActiveStep(parseInt(n))} style={{ cursor: stepStatus(parseInt(n)) !== 'locked' ? 'pointer' : 'default' }}>
            <StepBadge num={n} label={label} status={stepStatus(parseInt(n))} />
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title" style={{ fontSize: 15, marginBottom: 16 }}>
          {[
          '',
          'Step 1: Import Matters',
          'Step 2: Import Contacts',
          'Step 3: Import Activities',
          'Step 4: Import Bills',
          'Step 5: Import Trust Transactions'][
          activeStep]}
        </div>
        {activeStep === 1 && stepStatus(1) !== 'locked' && <MattersStep session={activeSession} onComplete={async () => {await refreshSession();setActiveStep(2);}} />}
        {activeStep === 2 && stepStatus(2) !== 'locked' && <ContactsStep session={activeSession} onComplete={async () => {await refreshSession();setActiveStep(3);}} />}
        {activeStep === 3 && stepStatus(3) !== 'locked' && <ActivitiesStep session={activeSession} onComplete={async () => {await refreshSession();}} />}
        {activeStep === 4 && stepStatus(4) !== 'locked' && <BillsStep session={activeSession} onComplete={async () => {await refreshSession();}} />}
        {activeStep === 5 && stepStatus(5) !== 'locked' && <TrustStep session={activeSession} onComplete={async () => {await refreshSession();}} />}
        {stepStatus(activeStep) === 'locked' && <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Complete the previous step to unlock this one.</p>}
      </div>

      {activeSession.clients?.length > 0 &&
      <>
          <div className="section-head">Clients in This Session ({activeSession.clients.length})</div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Name</th><th style={th}>Matter No.</th><th style={th}>Email</th><th style={th}>Phone</th></tr></thead>
              <tbody>{activeSession.clients.map((c, i) =>
              <tr key={c.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ ...td, fontWeight: 500 }}>{c.name}</td>
                  <td style={td}>{c.matter_number || '—'}</td>
                  <td style={{ ...td, color: c.email?.includes('@pending.import') ? '#e67e22' : '#333' }}>
                    {c.email?.includes('@pending.import') ? <em style={{ color: '#e67e22' }}>Pending — import contacts</em> : c.email || '—'}
                  </td>
                  <td style={td}>{c.phone ? c.phone : '—'}</td>
                </tr>
              )}</tbody>
            </table>
          </div>
        </>
      }
    </div>);


  return (
    <div>
      <h1 className="page-title">Clio Import</h1>
      <p className="page-subtitle">Import your Clio data in three sequential steps: Matters, Contacts, then Activities. Sessions are saved so you can pause and return.</p>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-title" style={{ fontSize: 15, marginBottom: 12 }}>Start New Import Session</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0, flex: 1 }}>
            <label>Session Name</label>
            <input type="text" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. May 2026 Import" onKeyDown={(e) => e.key === 'Enter' && createSession()} />
          </div>
          <button className="btn btn-primary" onClick={createSession} disabled={creating || !newLabel.trim()}>{creating ? 'Creating...' : 'Start Import'}</button>
        </div>
      </div>

      <div className="section-head">Previous Import Sessions</div>
      {loading && <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading...</div>}
      {!loading && sessions.length === 0 &&
      <div className="card" style={{ textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>No import sessions yet.</div>
      }
      {!loading && sessions.length > 0 &&
      <>
          {/* Desktop table */}
          <div className="desktop-only table-wrap">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Session</th>
                    <th style={th}>Clients</th>
                    <th style={th}>Matters</th>
                    <th style={th}>Contacts</th>
                    <th style={th}>Activities</th>
                    <th style={th}>Bills</th>
                    <th style={th}>Trust</th>
                    <th style={th}>Created</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s, i) => {
                  const complete = s.matters_imported_at && s.contacts_imported_at && s.activities_imported_at &&
                  s.bills_imported_at && s.trust_imported_at;
                  const partial = s.matters_imported_at && !complete;
                  return (
                    <tr key={s.id} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ ...td, fontWeight: 500 }}>
                          {s.label}
                          {complete && <span style={{ marginLeft: 8, fontSize: 10, background: '#0F1838', color: '#fff', borderRadius: 3, padding: '1px 6px' }}>Complete</span>}
                          {partial && <span style={{ marginLeft: 8, fontSize: 10, background: '#2f5496', color: '#fff', borderRadius: 3, padding: '1px 6px' }}>In Progress</span>}
                        </td>
                        <td style={td}>{s.client_count}</td>
                        <td style={td}>{s.matters_imported_at ? '✓' : '—'}</td>
                        <td style={td}>{s.contacts_imported_at ? '✓' : '—'}</td>
                        <td style={td}>{s.activities_imported_at ? '✓' : '—'}</td>
                        <td style={td}>{s.bills_imported_at ? '✓' : '—'}</td>
                        <td style={td}>{s.trust_imported_at ? '✓' : '—'}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        <td style={td}><button className="btn btn-sm" onClick={() => openSession(s.id)}>{complete ? 'View' : 'Continue'}</button></td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="mobile-only">
            {sessions.map((s) => {
            const complete = s.matters_imported_at && s.contacts_imported_at && s.activities_imported_at &&
            s.bills_imported_at && s.trust_imported_at;
            const partial = s.matters_imported_at && !complete;
            return (
              <div key={s.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                        {s.label}
                        {complete && <span style={{ marginLeft: 8, fontSize: 10, background: '#0F1838', color: '#fff', borderRadius: 3, padding: '1px 6px' }}>Complete</span>}
                        {partial && <span style={{ marginLeft: 8, fontSize: 10, background: '#2f5496', color: '#fff', borderRadius: 3, padding: '1px 6px' }}>In Progress</span>}
                      </div>
                      <div style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#555' }}>
                        {s.client_count} clients &bull; M {s.matters_imported_at ? '✓' : '—'} &bull; C {s.contacts_imported_at ? '✓' : '—'} &bull; A {s.activities_imported_at ? '✓' : '—'} &bull; B {s.bills_imported_at ? '✓' : '—'} &bull; T {s.trust_imported_at ? '✓' : '—'}
                      </div>
                      <div style={{ fontSize: 11, color: '#aaa', fontFamily: 'Arial, sans-serif', marginTop: 2 }}>
                        {new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                    <button className="btn btn-sm" onClick={() => openSession(s.id)}>{complete ? 'View' : 'Continue'}</button>
                  </div>
                </div>);

          })}
          </div>
        </>
      }
    </div>);

}